import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  useColorModeValue,
  Spinner,
  Center,
  Button,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBackIcon } from "@chakra-ui/icons";
import CryptoTransactionsTable from "views/admin/dataTables/components/CryptoTransactionsTable";
import useEndpoints from "providers/Endpoints";
import constants from "providers/Constants";
import { useSearch } from "contexts/SearchContext";

export default function CryptoTransactions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { network, address } = useParams();
  const endpoints = useEndpoints();
  const { searchQuery } = useSearch();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgColor = useColorModeValue("white", "gray.800");

  useEffect(() => {
    if (network && address) {
      getCryptoTransactions(network, address, 1);
    } else {
      setLoading(false);
    }
  }, [network, address]);

  const getCryptoTransactions = useCallback((net, addr, page = 1) => {
    setLoading(true);
    endpoints.getCryptoTransactions(net, addr, "completed", page, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t("FAILED"),
          error?.message || t("Snap! Something went wrong, Try again")
        );
        setTransactions([]);
      } else {
        setTransactions(data || []);
      }
    });
  }, [endpoints, t]);

  const handleGoBack = useCallback(() => {
    // Navigate back with network and address params to maintain selection
    navigate(`/admin/wallets?network=${network}&address=${address}`);
  }, [navigate, network, address]);

  if (!network || !address) {
    return (
      <Box pt={{ base: "180px", md: "80px", xl: "80px" }} px="20px">
        <Flex align="center" gap={4} mb="30px">
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            variant="ghost"
            colorScheme="brand"
          >
            {t("Back to Wallets")}
          </Button>
          <Text fontSize="2xl" fontWeight="700" color={textColor}>
            {t("Transactions")}
          </Text>
        </Flex>
        <Center height="200px">
          <Text color={textColor} fontSize="sm" fontWeight="500">
            {t("No wallet selected. Please select a wallet from the crypto wallets page.")}
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }} px="20px" pb="40px">
      {/* Header Section */}
      <Flex align="center" gap={4} mb="30px">
        <Button
          leftIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          variant="outline"
          colorScheme="brand"
          size="sm"
        >
          {t("Back")}
        </Button>
      </Flex>

      {/* Main Content */}
      <SimpleGrid mb="20px" columns={{ sm: 1, md: 1 }} spacing={{ base: "20px", xl: "20px" }}>
        {loading ? (
          <Flex justifyContent="center" alignItems="center" height="50vh">
            <Spinner size="xl" color="brand.500" thickness="4px" />
          </Flex>
        ) : (
          <Box bg={bgColor} borderRadius="16px">
            <CryptoTransactionsTable
              sortArray={[5, 10, 20, 50]}
              index={() => {}}
              tableData={transactions}
              searchQuery={searchQuery}
            />
          </Box>
        )}
      </SimpleGrid>
    </Box>
  );
}
